import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PaperPlaneRight, Calendar, ChatCircleDots, UserCircle, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { messagesApi } from "@/lib/jobsApi";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Messages hub — /messages lists all threads, /messages/:appId shows a single thread.
 */
export default function Messages() {
  const { appId } = useParams();
  return appId ? <Thread appId={appId} /> : <ThreadsList />;
}

function ThreadsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    messagesApi.threads().then((d) => setItems(d.items || [])).catch(() => toast.error("Failed to load messages")).finally(() => setLoading(false));
  }, []);
  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-3" data-testid="messages-list-page">
      <button onClick={() => navigate("/jobs")} className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
        <ArrowLeft size={14} weight="bold" /> Back to Jobs
      </button>
      <h1 className="font-heading text-3xl md:text-4xl font-black">Messages</h1>
      <p className="text-white/60 text-sm">Chat with employers or candidates after an application is submitted.</p>
      {loading && <div className="text-white/50">Loading…</div>}
      {!loading && items.length === 0 && (
        <Card className="bg-white/[0.03] border-white/10">
          <CardContent className="p-8 text-center text-white/50">
            <ChatCircleDots size={32} className="mx-auto mb-2 text-white/30" weight="duotone" />
            No conversations yet.
          </CardContent>
        </Card>
      )}
      {items.map((t) => (
        <Card
          key={t.application_id}
          data-testid={`thread-${t.application_id}`}
          className="bg-white/[0.03] border-white/10 cursor-pointer hover:bg-white/[0.06] transition"
          onClick={() => navigate(`/messages/${t.application_id}`)}
        >
          <CardContent className="p-4 flex items-center gap-3">
            {t.other_user?.picture ? (
              <img src={t.other_user.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                <UserCircle size={28} className="text-white/40" weight="duotone" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 justify-between">
                <div className="font-heading font-bold">{t.other_user?.name || "User"}</div>
                <div className="text-xs text-white/40">{new Date(t.last_message.created_at).toLocaleDateString()}</div>
              </div>
              <div className="text-xs text-white/50">Re: {t.job_title || "Job"}</div>
              <div className="text-sm text-white/70 truncate mt-1">
                {t.last_message.message_type === "interview_invite" && "📅 "}
                {t.last_message.body}
              </div>
            </div>
            {t.unread_count > 0 && (
              <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">
                {t.unread_count}
              </span>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Thread({ appId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [context, setContext] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [interviewAt, setInterviewAt] = useState("");
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await messagesApi.list(appId);
      setMsgs(d.items || []);
      setContext(d.application || null);
    } catch { toast.error("Cannot load conversation"); navigate("/messages"); }
  }, [appId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [msgs]);
  useEffect(() => {
    // Poll for new messages every 4s while this page is open
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const send = async (extra = {}) => {
    const text = extra.body ?? body;
    if (!text.trim()) return;
    setSending(true);
    try {
      const m = await messagesApi.send(appId, { body: text.trim(), ...extra });
      setMsgs((prev) => [...prev, m]);
      setBody("");
      if (extra.message_type === "interview_invite") {
        setShowInvite(false); setInterviewAt("");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const sendInvite = () => {
    if (!interviewAt) { toast.error("Pick a date & time"); return; }
    const when = new Date(interviewAt).toLocaleString();
    send({
      body: `Interview invitation for ${when}. Please confirm you can make it.`,
      message_type: "interview_invite",
      interview_at: new Date(interviewAt).toISOString(),
    });
  };

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 h-[calc(100vh-4rem)] flex flex-col" data-testid="thread-page">
      <button onClick={() => navigate("/messages")} className="text-white/60 hover:text-white flex items-center gap-1 text-sm mb-2">
        <ArrowLeft size={14} weight="bold" /> All messages
      </button>
      <div className="mb-3">
        <h1 className="font-heading text-xl font-black">Conversation</h1>
        {context && (
          <p className="text-xs text-white/50">
            Application <span className="text-white/70 font-mono">{appId.slice(-8)}</span> · <button onClick={() => navigate(`/applications/${appId}`)} className="text-blue-300 hover:underline">view details</button>
          </p>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-2">
        {msgs.length === 0 && <div className="text-center text-white/40 py-8">No messages yet — say hi 👋</div>}
        {msgs.map((m) => {
          const mine = m.from_user_id === user?.user_id;
          const isInvite = m.message_type === "interview_invite";
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`msg-${m.message_id}`}
                className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                  isInvite
                    ? "bg-yellow-500/15 border border-yellow-500/40 text-yellow-100"
                    : mine ? "bg-blue-600 text-white" : "bg-white/10 text-white/90"
                }`}
              >
                {isInvite && (
                  <div className="text-[10px] uppercase tracking-widest font-bold mb-1 flex items-center gap-1">
                    <Calendar size={11} weight="fill" /> Interview
                  </div>
                )}
                <div className="whitespace-pre-line">{m.body}</div>
                {m.interview_at && (
                  <div className="mt-1 text-[11px] opacity-80 flex items-center gap-1">
                    <Clock size={10} weight="bold" /> {new Date(m.interview_at).toLocaleString()}
                  </div>
                )}
                <div className="text-[10px] opacity-60 mt-1">
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showInvite && (
        <div className="mt-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex gap-2 items-center flex-wrap">
          <Calendar size={16} weight="fill" className="text-yellow-300" />
          <Input data-testid="thread-interview-datetime" type="datetime-local" value={interviewAt} onChange={(e) => setInterviewAt(e.target.value)} className="bg-white/5 border-white/10 flex-1" />
          <Button data-testid="thread-invite-send" onClick={sendInvite} disabled={sending} className="bg-yellow-600 hover:bg-yellow-700">Send invite</Button>
          <Button variant="ghost" onClick={() => setShowInvite(false)}>Cancel</Button>
        </div>
      )}

      <div className="mt-2 flex gap-2">
        <Input
          data-testid="thread-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
          placeholder="Type a message…"
          className="bg-white/5 border-white/10"
        />
        <Button data-testid="thread-invite-toggle" variant="outline" onClick={() => setShowInvite((v) => !v)} className="border-yellow-500/40 bg-transparent hover:bg-yellow-500/10 text-yellow-300 gap-1">
          <Calendar size={14} weight="bold" /> Interview
        </Button>
        <Button data-testid="thread-send" onClick={() => send()} disabled={sending || !body.trim()} className="bg-blue-600 hover:bg-blue-700 gap-1">
          <PaperPlaneRight size={16} weight="fill" /> Send
        </Button>
      </div>
    </div>
  );
}
